import { shipmentDocumentTypes } from "../til-enums";

// The enums of the documents domain that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/**
 * `documents.document_type`. In F1 Nexxt asks Business Central only for `invoice` and `proforma` (I-04, ADR-096),
 * from F2 also for the `credit_note` Finance approved (ADR-230): the other types arrive with the step that requests
 * them. `export_pack` (F2, B-53, ADR-232): the pack of an export shipment the PDF manager makes and TIL brings with
 * I-07, nobody in Nexxt asks for it.
 */
export const documentTypes = [
  "order_confirmation",
  "proforma",
  "invoice",
  "credit_note",
  "outbound_ddt",
  "inbound_ddt",
  "packing_list",
  "export_declaration",
  "purchase_order_pdf",
  "export_pack",
] as const;
export type DocumentType = (typeof documentTypes)[number];

/** The types a process asks Business Central to register for a shipment: the ones I-04 carries. */
export const bcDocumentTypes = shipmentDocumentTypes satisfies readonly DocumentType[];
export type BcDocumentType = (typeof bcDocumentTypes)[number];

/**
 * `documents.document_status` (spec v0.2 §5, FR-M9-07): requested → numbered → pdf_available → sent, `error` from
 * any of them and relaunched with the same key. For a document Nexxt numbers (C-48, ADR-255) `requested` is "numbered
 * by Nexxt and sent to BC" and `numbered` is "registered by BC" (spec v0.3 §5.3). `not_generated` is the state of a
 * document nobody asked for yet: Nexxt never writes it, a document is born when it is asked for (ADR-118). `sent` with
 * notifications (P5.7).
 */
export const documentStatuses = [
  "not_generated",
  "requested",
  "numbered",
  "pdf_available",
  "sent",
  "error",
  "cancelled",
] as const;
export type DocumentStatus = (typeof documentStatuses)[number];

/**
 * `documents.credit_note_reason` (F2, B-54 point 3, ADR-230): what a credit note credits, and the domain where Finance
 * approved it. To a customer: a shipment closed short (`sales.short_shipment`), a claim closed with a credit note
 * (`claims.claim`). From a supplier: the units an inbound missed (`procurement.credit_note_request`), a vendor return
 * (`logistics.stock_movement`).
 */
export const creditNoteReasons = ["short_shipment", "claim", "inbound_shortage", "vendor_return"] as const;
export type CreditNoteReason = (typeof creditNoteReasons)[number];
